<template>
  <Dropdown :placement :shown auto-boundary-max-size>
    <UIButton
      v-tooltip="{ content: 'Columns config', placement: 'top' }"
      v-bind="$attrs"
      :disabled
      :label="buttonLabel"
      :leading="ViewColumnsIcon"
      variant="gray"
      fill="light"
      data-refid="columns-config-button"
      @click="handleToggle"
    />
    <template #popper>
      <div
        ref="popupRef"
        v-circular-tab
        class="ui-columns-config"
        :data-refid="dataRefId"
      >
        <draggable
          v-model="computedValue"
          v-bind="dragOptions"
          handle=".handle"
          item-key="field"
          tag="div"
          class="ui-columns-config__list"
        >
          <template #item="{ element }">
            <ColumnsConfigItem
              :label="getFieldName(element.field)"
              :model-value="element.enabled"
              @update:model-value="handleUpdate(element.field, $event)"
            />
          </template>
        </draggable>
      </div>
    </template>
  </Dropdown>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, ref, watchEffect } from 'vue'
import { Dropdown, Placement } from 'floating-vue'
import draggable from 'vuedraggable'
import { orderBy } from 'lodash'

import { GridColumn } from '@types'
import { ColumnsConfig } from './utils/types'

import { UNKNOWN_VALUE } from '@/const'

import { useHotKeys } from '@/plugins/hotKeys'

import { UIButton } from '@ui/buttons'
import { ViewColumnsIcon } from '@heroicons/vue/24/outline'
import ColumnsConfigItem from './ColumnsConfigItem.vue'

type Props = {
  columns: GridColumn[]
  buttonLabel?: string
  placement?: Placement
  disabled?: boolean
  disallowDrag?: boolean
}

const props = defineProps<Props>()

const modelValue = defineModel<GridColumn[]>()
const modelStorage = defineModel<ColumnsConfig[]>('storage')

const shown = ref(false)

const uniqueId = computed(() => getCurrentInstance()?.uid.toString())
const dataRefId = computed(() => `columns-config-popper-${uniqueId.value}`)

const dragOptions = computed(() => ({
  animation: 200,
  disabled: props.disallowDrag,
}))

const computedValue = computed({
  get() {
    return orderBy(props.columns, column => modelStorage.value?.findIndex(item => item.field === column.name)).map(column => ({
      field: column.name,
      enabled: modelStorage.value ? !!modelStorage.value.find(item => item.field === column.name)?.enabled : true,
    }))
  },
  set(value) {
    modelStorage.value = value
  },
})

const getFieldName = (field: string) => {
  return props.columns.find(column => column.name === field)?.caption || UNKNOWN_VALUE
}

const handleToggle = () => {
  shown.value = !shown.value
}

const handleUpdate = (field: string, enabled: boolean) => {
  modelStorage.value = computedValue.value?.map(item => item.field === field ? { ...item, enabled } : item)
}

useHotKeys(
  'escape',
  e => {
    e?.stopPropagation()
    shown.value = false
    return true
  },
  { priority: 100 },
)

watchEffect(() => {
  modelValue.value = computedValue.value.filter(item => item.enabled).map(item => props.columns.find(column => column.name === item.field)).filter(item => !!item)
})
</script>

<style scoped>
.ui-columns-config {
  @apply py-3 px-4;

  &__list {
    @apply flex flex-col;
    @apply gap-2;
  }
}
</style>
